import {
  ChangeDetectionStrategy,
  Component,
  inject,
  computed,
  viewChild,
  output,
  input,
} from '@angular/core';
import { ModalDialogComponent, ModalDialogService } from '@pedix-workspace/angular-ui-modal';
import { AnalyticsService } from '@pedix-workspace/pedixapp-core-services';
import {
  IconFacebookComponent,
  IconHomeComponent,
  IconInfoComponent,
  IconInstagramComponent,
  IconMapMarkerComponent,
  IconStoreComponent,
  IconTiktokComponent,
  IconTwitterComponent,
  IconWebComponent,
} from '@pedix-workspace/angular-ui-icons';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { environment } from '../../../../environments/environment';
import { AppRequestContextService } from '../../../app-request-context.service';
import { SubsidiariesSelectorDialogComponent } from '../../establishment/subsidiaries/subsidiaries-selector-dialog/subsidiaries-selector-dialog.component';
import { EstablishmentInfoComponent } from './establishment-info/establishment-info.component';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    RouterLink,
    IconHomeComponent,
    IconInfoComponent,
    IconMapMarkerComponent,
    IconWebComponent,
    IconInstagramComponent,
    IconFacebookComponent,
    IconTwitterComponent,
    IconTiktokComponent,
    IconStoreComponent,
    ModalDialogComponent,
    EstablishmentInfoComponent,
  ],
})
export class SideMenuComponent {
  private appRequestContextService = inject(AppRequestContextService);
  private analytics = inject(AnalyticsService);
  private modalDialogService = inject(ModalDialogService);

  readonly infoDialog = viewChild.required<ModalDialogComponent>('infoDialog');
  readonly locationDialog = viewChild.required<ModalDialogComponent>('locationDialog');

  readonly navItemClick = output<void>();
  readonly subsidiarySelected = output<string>();

  readonly selectedSubsidiaryId = input<string | null>();

  readonly establishment = computed(() => this.appRequestContextService.establishment());
  readonly subsidiaries = computed(() => this.appRequestContextService.subsidiaries());
  readonly subsidiarySelector = computed(() => this.appRequestContextService.subsidiarySelector());
  readonly workingDaysEnabled = computed(() => this.establishment().workingDays.isActive);
  readonly hasContactInfo = computed(() => {
    return this.establishment().phone || this.establishment().additionalPhone;
  });
  readonly infoEnabled = computed(() => {
    const workingDaysEnabled = this.workingDaysEnabled();
    const hasContactInfo = this.hasContactInfo();

    return workingDaysEnabled || hasContactInfo;
  });
  readonly locationEnabled = computed(() => {
    const addressDetails = this.establishment().addressDetails;
    const subsidiarySelector = this.subsidiarySelector();

    return addressDetails && !subsidiarySelector;
  });
  readonly hasFeatureWhiteLabelBranding = computed(() =>
    this.establishment().features.includes('WHITE_LABEL_BRANDING'),
  );

  get landingUrl() {
    return `${environment.landingUrl}?utm_source=app&utm_medium=menu&utm_campaign=interest`;
  }

  onNavItemClick(event) {
    this.navItemClick.emit();
  }

  onClickInfo() {
    this.infoDialog()
      .open()
      .catch(() => {});
  }

  onClickLocation() {
    this.locationDialog()
      .open()
      .catch(() => {});
  }

  async onClickSubsidiaries() {
    const selectedSubsidiaryId = await this.modalDialogService
      .attachDialog(SubsidiariesSelectorDialogComponent)
      .open({
        subsidiaries: this.appRequestContextService.subsidiaries(),
        selectedSubsidiaryId: this.selectedSubsidiaryId(),
        displaySubsidiariesGroupSelector: true,
      })
      .catch(() => {});

    if (selectedSubsidiaryId) {
      this.subsidiarySelected.emit(selectedSubsidiaryId);
    }
  }

  onInterestInPedix() {
    this.analytics.logEvent('sidemenupedix_interest_clicked');
  }
}
